import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Card, 
  Typography, 
  Space, 
  Button, 
  message, 
  Popconfirm,
  Tag,
  Modal,
  Input
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { commentService } from '../../services/commentService';

import { Comment } from '../../types';
import { formatLocalizedDateTime } from '../../utils/dateUtils';

// 입법예고 마감일 체크 함수
const isDeadlinePassed = (deadlineDate: string): boolean => {
  const deadline = new Date(deadlineDate + 'T00:00:00.000Z');
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  today.setHours(0, 0, 0, 0);
  
  return today > deadline;
};

// 간단한 날짜 시간 포맷 함수
const formatSimpleDateTime = (dateString: string, t: any): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  // 오늘
  if (diffDays === 0) {
    return date.toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  }
  
  // 어제
  if (diffDays === 1) {
    return t('comments.dateFormat.yesterday');
  }
  
  // 7일 이내
  if (diffDays < 7) {
    return t('comments.dateFormat.daysAgo', { count: diffDays });
  }
  
  // 30일 이내
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return t('comments.dateFormat.weeksAgo', { count: weeks });
  }
  
  // 1년 이내
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return t('comments.dateFormat.monthsAgo', { count: months });
  }
  
  // 1년 이상
  const years = Math.floor(diffDays / 365);
  return t('comments.dateFormat.yearsAgo', { count: years });
};

const { Title } = Typography;
const { TextArea } = Input;

const CommentList: React.FC = () => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editContent, setEditContent] = useState('');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await commentService.getMyComments();
      setComments(data);
    } catch (error) {
      message.error(t('comments.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleEdit = (comment: Comment) => {
    // 입법예고 댓글의 마감일이 지났으면 수정 불가
    if (isLegislativeNoticeDeadlinePassed(comment)) {
      message.warning(t('comments.deadlinePassedWarning'));
      return;
    }
    
    setEditingComment(comment);
    setEditContent(comment.content);
    setEditModalVisible(true);
  };

  const handleSaveEdit = async () => {
    if (!editingComment || !editContent.trim()) return;
    
    try {
      await commentService.updateComment(editingComment.comment_id, {
        content: editContent.trim()
      });
      message.success(t('comments.updateSuccess'));
      setEditModalVisible(false);
      setEditingComment(null);
      setEditContent('');
      fetchComments();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('comments.updateFailed'));
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await commentService.deleteComment(commentId);
      message.success(t('comments.deleteSuccess'));
      fetchComments();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('comments.deleteFailed'));
    }
  };

  const handleViewDocument = (comment: Comment) => {
    if (comment.announcement) {
      navigate(`/announcements/${comment.announcement.announcement_id}`);
    } else if (comment.legislativeNotice) {
      navigate(`/legislative-notices/${comment.legislativeNotice.legislative_notice_id}`);
    }
  };

  const getDocumentType = (comment: Comment) => {
    if (comment.announcement) {
      return { type: t('comments.documentTypes.announcement'), color: 'blue' };
    } else if (comment.legislativeNotice) {
      return { type: t('comments.documentTypes.legislativeNotice'), color: 'green' };
    }
    return { type: t('comments.documentTypes.unknown'), color: 'default' };
  };

  const isLegislativeNoticeDeadlinePassed = (comment: Comment): boolean => {
    if (comment.legislativeNotice && (comment.legislativeNotice as any).deadline_date) {
      return isDeadlinePassed((comment.legislativeNotice as any).deadline_date);
    }
    return false;
  };

  const columns = [
    {
      title: t('comments.table.number'),
      dataIndex: 'comment_id',
      key: 'comment_id',
      width: 60,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => comments.length - index,
    },
    {
      title: t('comments.table.documentType'),
      key: 'document_type',
      width: 100,
      align: 'center' as const,
      render: (record: Comment) => {
        const { type, color } = getDocumentType(record);
        const isDeadlinePassed = isLegislativeNoticeDeadlinePassed(record);
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
            <Tag color={color}>{type}</Tag>
            {record.legislativeNotice && isDeadlinePassed && (
              <Tag color="red" style={{ fontSize: '10px', padding: '0 4px' }}>
                {t('comments.status.deadline')}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: t('comments.table.content'),
      dataIndex: 'content',
      key: 'content',
      width: 400,
      ellipsis: true,
      render: (content: string) => (
        <div style={{ 
          maxWidth: '100%', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap' 
        }}>
          {content}
        </div>
      ),
    },
    {
      title: (
        <div style={{ textAlign: 'center' }}>
          <div>{t('comments.table.createdAt')}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>({t('comments.table.updatedAt')})</div>
        </div>
      ),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      align: 'center' as const,
      render: (date: string, record: Comment) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
          <span title={formatLocalizedDateTime(date, i18n.language)} style={{ fontSize: '12px' }}>
            {formatSimpleDateTime(date, t)}
          </span>
          {record.updated_at && record.updated_at !== record.created_at && (
            <span title={formatLocalizedDateTime(record.updated_at, i18n.language)} style={{ fontSize: '11px', color: '#666' }}>
              ({formatSimpleDateTime(record.updated_at, t)})
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('comments.table.actions'),
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (record: Comment) => {
        const isDeadlinePassed = isLegislativeNoticeDeadlinePassed(record);
        
        return (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDocument(record)}
              title={t('comments.table.viewDocument')}
            />
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              title={isDeadlinePassed ? t('comments.table.cannotEditDeadline') : t('comments.table.edit')}
              disabled={isDeadlinePassed}
            />
            <Popconfirm
              title={t('comments.table.deleteConfirm')}
              onConfirm={() => handleDelete(record.comment_id)}
              okText={t('comments.table.delete')}
              cancelText={t('comments.modal.cancel')}
              disabled={isDeadlinePassed}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                title={isDeadlinePassed ? t('comments.table.cannotDeleteDeadline') : t('comments.table.delete')}
                disabled={isDeadlinePassed}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '16px' }}>
      <Card style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            {t('comments.title', { count: comments.length })}
          </Title>
          <Space>
            <span style={{ color: '#666' }}>
              {t('comments.totalComments', { count: comments.length })}
            </span>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={comments}
          rowKey="comment_id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('comments.pagination.showTotal', { 
              start: range[0], 
              end: range[1], 
              total: total 
            }),
          }}
          scroll={{ x: 800 }}
          size="small"
          style={{
            fontSize: '14px'
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <MessageOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <div style={{ color: '#666' }}>{t('comments.empty.title')}</div>
                <div style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                  {t('comments.empty.description')}
                </div>
              </div>
            )
          }}
        />
      </Card>

      {/* 댓글 수정 모달 */}
      <Modal
        title={t('comments.modal.editTitle')}
        open={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingComment(null);
          setEditContent('');
        }}
        okText={t('comments.modal.save')}
        cancelText={t('comments.modal.cancel')}
        width={600}
      >
        {editingComment && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t('comments.modal.documentType')}:</strong> {getDocumentType(editingComment).type}
              {editingComment.legislativeNotice && isLegislativeNoticeDeadlinePassed(editingComment) && (
                <Tag color="red" style={{ marginLeft: 8 }}>{t('comments.status.deadlinePassed')}</Tag>
              )}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>{t('comments.modal.createdAt')}:</strong> {formatSimpleDateTime(editingComment.created_at, t)}
            </div>
            {editingComment.legislativeNotice && (editingComment.legislativeNotice as any).deadline_date && (
              <div style={{ marginBottom: 16 }}>
                <strong>{t('comments.modal.deadline')}:</strong> {(editingComment.legislativeNotice as any).deadline_date}
                {isLegislativeNoticeDeadlinePassed(editingComment) && (
                  <span style={{ color: 'red', marginLeft: 8 }}>({t('comments.status.deadlinePassed')})</span>
                )}
              </div>
            )}
            <TextArea
              rows={6}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder={t('comments.modal.contentPlaceholder')}
              style={{ marginBottom: 16 }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CommentList; 